import { useState, useEffect, useCallback } from "react";
import { Table, Pagination } from "antd";
import { fetchDiscountCodeHistory } from "services/discountCodes.service";
import { renderErrorNotifications } from "helpers/error.helpers";
import {
  CODES_PAGE_SIZE,
  formatHistoryField,
  formatHistoryValue,
  formatIst,
} from "../commons/discountCodes.helpers";
import { CODES_COPY } from "../commons/discountCodes.copy";

const columns = [
  {
    title: "When",
    dataIndex: "at",
    key: "at",
    render: (v) => formatIst(v, "D MMM YYYY, h:mm a"),
  },
  {
    title: "Who",
    key: "who",
    render: (_, r) =>
      r.actor_type === "SHOUP_OPS"
        ? CODES_COPY.historyShoupTeam
        : r.actor_name || "—",
  },
  {
    title: "Field",
    dataIndex: "field",
    key: "field",
    render: (v) => formatHistoryField(v),
  },
  {
    title: "Old value",
    key: "old_value",
    render: (_, r) =>
      r.field === "created" ? "—" : formatHistoryValue(r.field, r.old_value),
  },
  {
    title: "New value",
    key: "new_value",
    render: (_, r) =>
      r.field === "created" ? "—" : formatHistoryValue(r.field, r.new_value),
  },
];

/** Change history of the code, newest first (R-15, D-8). */
function HistoryTab({ codeId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, total: 0 });

  const load = useCallback(
    async (page) => {
      setLoading(true);
      const { data: envelope, errors } = await fetchDiscountCodeHistory(
        codeId,
        { current_page: page, page_size: CODES_PAGE_SIZE }
      );
      setLoading(false);
      if (errors?.length) {
        renderErrorNotifications(errors);
        setRows([]);
        return;
      }
      setRows(Array.isArray(envelope?.data) ? envelope.data : []);
      setPagination({
        current: page,
        total: envelope?.meta?.pagination?.total_count ?? 0,
      });
    },
    [codeId]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  // Only the "created" entry means nothing has changed yet.
  const isOnlyCreated = rows.length === 1 && rows[0].field === "created";

  return (
    <div className="space-y-4">
      <Table
        rowKey={(r, i) => `${r.at ?? ""}-${r.field ?? ""}-${i}`}
        columns={columns}
        dataSource={isOnlyCreated ? [] : rows}
        loading={loading}
        size="small"
        scroll={{ x: 700 }}
        locale={{ emptyText: CODES_COPY.historyEmpty }}
        // A page can hold more rows than page_size (pagination counts change
        // events), so the table never slices; paging is server side below.
        pagination={false}
      />
      {pagination.total > CODES_PAGE_SIZE && (
        <div className="flex justify-end">
          <Pagination
            current={pagination.current}
            pageSize={CODES_PAGE_SIZE}
            total={pagination.total}
            showSizeChanger={false}
            onChange={(page) => load(page)}
          />
        </div>
      )}
    </div>
  );
}

export default HistoryTab;
